import { useMemo, useState } from 'react';
import { Clock, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useApp } from '@/context/AppContext';
import { cn } from '@/lib/utils';
import { useDeadlines } from '@/hooks/useDeadlines';
import { isOpen } from '@trackmind/core';

interface DeadlinesWidgetProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  /**
   * Open the task. Optional so the widget still renders (inert, as before) if
   * a future host doesn't have a detail dialog to show.
   */
  onOpenTask?: (taskId: string) => void;
}

interface DeadlineTask {
  id: string;
  title: string;
  deadline: Date;
  daysUntil: number;
  projectName?: string;
  priority: number;
}

/**
 * Deadlines you can act on.
 *
 * Every row here used to be a plain <div>: it told you a deadline was coming
 * and gave you no way to do anything about it, so the next move was to
 * remember the title, go to the project or search, and find it by hand. The
 * rows are buttons now and open the same task dialog the rest of Today uses —
 * complete, edit, reschedule, all one tap in.
 *
 * The rows also gained the date itself. "Tomorrow" is a group heading, but for
 * anything past that "In 3 days" made you count, and the actual date is two
 * words. Overdue rows say how far overdue rather than just glowing red.
 */
export function DeadlinesWidget({ isCollapsed, onToggleCollapse, onOpenTask }: DeadlinesWidgetProps) {
  const { tasks, projects } = useApp();
  const dl = useDeadlines();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const upcomingDeadlines = useMemo(() => {
    const deadlines: DeadlineTask[] = [];

    for (const task of tasks) {
      // Open work only — the same test as every other "due" list.
      if (!isOpen(task) || task.is_habit) continue;

      // Core's reading, in the user's zone. The old `new Date(deadline)` and
      // `Math.ceil` put a quick-captured "tomorrow" on the wrong day for
      // anyone west of Greenwich in the evening, and a bare date one day
      // early ("Overdue") for the whole Americas.
      const daysUntil = dl.daysLeft(task);
      const deadline = dl.date(task);
      if (daysUntil === null || !deadline) continue;

      if (daysUntil <= 3) {
        const project = projects.find(p => p.id === task.project_id);

        deadlines.push({
          id: task.id,
          title: task.title,
          deadline,
          daysUntil,
          projectName: project?.name,
          priority: task.priority,
        });
      }
    }

    // Sort by deadline (soonest first), then by priority
    return deadlines.sort((a, b) => {
      if (a.daysUntil !== b.daysUntil) return a.daysUntil - b.daysUntil;
      return b.priority - a.priority;
    });
  }, [tasks, projects, dl]);

  // Group by days until deadline
  const groupedDeadlines = useMemo(() => {
    const groups: { label: string; color: string; tasks: DeadlineTask[] }[] = [];

    const overdue = upcomingDeadlines.filter(d => d.daysUntil < 0);
    const today = upcomingDeadlines.filter(d => d.daysUntil === 0);
    const tomorrow = upcomingDeadlines.filter(d => d.daysUntil === 1);
    const inTwoDays = upcomingDeadlines.filter(d => d.daysUntil === 2);
    const inThreeDays = upcomingDeadlines.filter(d => d.daysUntil === 3);

    if (overdue.length > 0) groups.push({ label: 'Overdue', color: 'text-red-500', tasks: overdue });
    if (today.length > 0) groups.push({ label: 'Today', color: 'text-orange-500', tasks: today });
    if (tomorrow.length > 0) groups.push({ label: 'Tomorrow', color: 'text-yellow-500', tasks: tomorrow });
    if (inTwoDays.length > 0) groups.push({ label: 'In 2 days', color: 'text-blue-500', tasks: inTwoDays });
    if (inThreeDays.length > 0) groups.push({ label: 'In 3 days', color: 'text-muted-foreground', tasks: inThreeDays });

    return groups;
  }, [upcomingDeadlines]);

  const hasUrgent = upcomingDeadlines.some(d => d.daysUntil <= 1);

  return (
    <Card className={cn("bg-card/50", hasUrgent && "border-red-500/30")}>
      <CardHeader className="py-2 px-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className={cn("h-4 w-4", hasUrgent && "text-red-500")} />
            Deadlines
            {upcomingDeadlines.length > 0 && (
              <Badge variant={hasUrgent ? "destructive" : "secondary"} className="text-xs px-1.5 py-0 h-5">
                {upcomingDeadlines.length}
              </Badge>
            )}
          </CardTitle>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onToggleCollapse}>
            {isCollapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          </Button>
        </div>
      </CardHeader>

      {!isCollapsed && (
        <CardContent className="py-2 px-3">
          {upcomingDeadlines.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-2">
              No deadlines in the next 3 days
            </p>
          ) : (
            <div className="space-y-3">
              {groupedDeadlines.map(group => (
                <div key={group.label}>
                  <div className={cn("text-xs font-medium mb-1 flex items-center gap-1", group.color)}>
                    {group.label === 'Overdue' && <AlertCircle className="h-3 w-3" />}
                    {group.label}
                    <span className="text-muted-foreground">({group.tasks.length})</span>
                  </div>
                  <div className="space-y-1">
                    {(expanded[group.label] ? group.tasks : group.tasks.slice(0, 3)).map(task => (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => onOpenTask?.(task.id)}
                        disabled={!onOpenTask}
                        // 44px minimum on touch; the desktop sidebar keeps the
                        // tighter row it already had.
                        className={cn(
                          "w-full text-left text-sm px-2 py-1.5 rounded bg-accent/30",
                          "min-h-[44px] md:min-h-0 flex flex-col justify-center",
                          onOpenTask && "cursor-pointer hover:bg-accent active:bg-accent",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          group.label === 'Overdue' && "bg-red-500/10 border border-red-500/20"
                        )}
                        title={
                          onOpenTask
                            ? `Open "${task.title}" — due ${task.deadline.toLocaleDateString()}`
                            : `Due ${task.deadline.toLocaleDateString()}`
                        }
                      >
                        <span className="truncate">{task.title}</span>
                        <span className="text-xs text-muted-foreground truncate">
                          {task.deadline.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          {task.daysUntil < 0 && ` · ${Math.abs(task.daysUntil)}d late`}
                          {task.projectName && ` · ${task.projectName}`}
                        </span>
                      </button>
                    ))}
                    {group.tasks.length > 3 && (
                      // Was static text, which meant a group of eight showed
                      // three and silently swallowed five.
                      <button
                        type="button"
                        onClick={() => setExpanded(prev => ({ ...prev, [group.label]: !prev[group.label] }))}
                        aria-expanded={Boolean(expanded[group.label])}
                        className="text-xs text-muted-foreground hover:text-foreground pl-2 min-h-[32px] md:min-h-0 text-left w-full"
                      >
                        {expanded[group.label] ? 'Show fewer' : `+${group.tasks.length - 3} more`}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
